/**
 * useRegistryApi — YAML-driven API hook.
 *
 * Reads endpoint definitions from domain registry YAML and returns
 * typed fetch functions. Replaces hardcoded APIEndpoints enum usage.
 *
 * Usage:
 *   const { call, loading, error } = useRegistryApi("auth", "login");
 *   const result = await call({ email: "demo@example.com", password: "pass" });
 */

import { useState, useCallback, useMemo } from "react";

import { apiBaseUrls } from "@platform/constants/shared-constants";

import { RegistryEndpoint } from "../config/domainRegistry";
import {
  useDomainRegistry,
  DomainName,
} from "../config/DomainRegistryProvider";

interface UseRegistryApiOptions {
  /** Override the base URL (defaults to VITE_API_URL env var). */
  baseUrl?: string;
}

interface UseRegistryApiReturn<TResponse = unknown> {
  /** The endpoint definition from the registry YAML. */
  endpoint: RegistryEndpoint | undefined;
  /** Whether a request is in flight. */
  loading: boolean;
  /** Error message from the last failed request. */
  error: string | null;
  /** Response data from the last successful request. */
  data: TResponse | null;
  /**
   * Execute the API call.
   * @param body  — request body (for POST/PATCH/PUT)
   * @param pathParams — URL path parameters (e.g., { id: "123" } for /api/v1/users/:id)
   * @param queryParams — URL query parameters
   */
  call: (options?: {
    body?: unknown;
    pathParams?: Record<string, string>;
    queryParams?: Record<string, string>;
    headers?: Record<string, string>;
  }) => Promise<TResponse>;
  /** Reset error and data state. */
  reset: () => void;
}

function resolvePathParams(
  path: string,
  params: Record<string, string>,
): string {
  let resolved = path;
  for (const [key, value] of Object.entries(params)) {
    resolved = resolved.replace(`:${key}`, encodeURIComponent(value));
  }
  return resolved;
}

function buildQueryString(params: Record<string, string>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== "",
  );
  if (entries.length === 0) return "";
  return "?" + new URLSearchParams(entries).toString();
}

function getBaseUrl(): string {
  return apiBaseUrls.BACKEND;
}

export function useRegistryApi<TResponse = unknown>(
  domain: DomainName,
  endpointName: string,
  options?: UseRegistryApiOptions,
): UseRegistryApiReturn<TResponse> {
  const { getEndpoint } = useDomainRegistry(domain);
  const endpoint = getEndpoint(endpointName);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TResponse | null>(null);

  const baseUrl = options?.baseUrl ?? getBaseUrl();

  const call = useCallback(
    async (callOptions?: {
      body?: unknown;
      pathParams?: Record<string, string>;
      queryParams?: Record<string, string>;
      headers?: Record<string, string>;
    }): Promise<TResponse> => {
      if (!endpoint) {
        const msg = `Endpoint "${endpointName}" not found in "${domain}" registry`;
        setError(msg);
        throw new Error(msg);
      }

      setLoading(true);
      setError(null);

      try {
        let url = `${baseUrl}${endpoint.path}`;

        // Resolve path params (:id, :orgAlias, etc.)
        if (callOptions?.pathParams) {
          url = resolvePathParams(url, callOptions.pathParams);
        }

        // Append query params
        if (callOptions?.queryParams) {
          url += buildQueryString(callOptions.queryParams);
        }

        // Build headers
        const headers: Record<string, string> = {
          "Content-Type": "application/json",
          ...(callOptions?.headers ?? {}),
        };

        const fetchOptions: RequestInit = {
          method: endpoint.method,
          headers,
          credentials: "include",
        };

        // Add body for non-GET methods
        if (
          callOptions?.body &&
          endpoint.method !== "GET" &&
          endpoint.method !== "DELETE"
        ) {
          fetchOptions.body = JSON.stringify(callOptions.body);
        }

        const response = await fetch(url, fetchOptions);

        if (!response.ok) {
          const errorBody = await response.json().catch(() => ({}));
          const errorMessage =
            (errorBody as Record<string, string>).error ||
            `Request failed: ${response.status} ${response.statusText}`;
          setError(errorMessage);
          setLoading(false);
          throw new Error(errorMessage);
        }

        const result = (await response.json()) as TResponse;
        setData(result);
        setLoading(false);
        return result;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Unknown error occurred";
        setError(message);
        setLoading(false);
        throw err;
      }
    },
    [endpoint, endpointName, domain, baseUrl],
  );

  const reset = useCallback(() => {
    setError(null);
    setData(null);
    setLoading(false);
  }, []);

  return useMemo(
    () => ({ endpoint, loading, error, data, call, reset }),
    [endpoint, loading, error, data, call, reset],
  );
}
